import { useTranslation } from 'react-i18next';

import {
  FiltersDrawer,
  PrimaryFiltersRow,
  SavedFiltersBadgeRow,
  UniversalFiltersProvider,
} from '~/modules/universalFilters';

import { getFormattedNumber } from '@vestigas/utils';

import { Alert, Button, Icon } from '@vestigas/ui';
import { DataManagementLayout } from '~/ui/layouts/DataManagementLayout';
import { ConfirmDialog } from '~/ui/molecules';

import { TaxCodeFormDialog } from './components/TaxCodeFormDialog';
import { TaxCodesDataTable } from './components/TaxCodesDataTable';
import { useTaxCodesPage } from './useTaxCodesPage';

const addIcon = <Icon metaphor="add" />;
const deleteIcon = <Icon metaphor="delete" />;

export function TaxCodesPage() {
  const { t } = useTranslation('taxCodes');
  const {
    bulkDeleteError,
    clearSelection,
    closeBulkDeleteConfirm,
    closeDialog,
    confirmBulkDelete,
    filterConfig,
    isBulkDeleteConfirmOpen,
    isBulkDeletePending,
    isDialogOpen,
    openBulkDeleteConfirm,
    openCreateDialog,
    rowSelectionModel,
    savedFiltersState,
    savedFilterUrlSync,
    selectedTaxCodeIds,
    setRowSelectionModel,
    taxCodeId,
  } = useTaxCodesPage();
  const selectedCount = selectedTaxCodeIds.length;
  const formattedSelectedCount = getFormattedNumber({
    value: selectedCount,
  });

  return (
    <UniversalFiltersProvider
      config={filterConfig}
      savedFilterUrlSync={savedFilterUrlSync}
      savedFiltersState={savedFiltersState}
    >
      <DataManagementLayout>
        <h1 className="pt-4 text-3xl">{t(($) => $.pageTitle)}</h1>

        <SavedFiltersBadgeRow />
        <PrimaryFiltersRow>
          {selectedCount > 0 ? (
            <Button
              color="error"
              icon={deleteIcon}
              text={t(($) => $.list.bulkDelete.buttonText, {
                count: selectedCount,
                formattedCount: formattedSelectedCount,
              })}
              onClick={openBulkDeleteConfirm}
            />
          ) : null}
          <Button
            icon={addIcon}
            text={t(($) => $.list.addButton.text)}
            onClick={openCreateDialog}
          />
        </PrimaryFiltersRow>

        {bulkDeleteError ? (
          <Alert className="mb-4" severity="error">
            {t(($) => $.list.bulkDelete.error)}
          </Alert>
        ) : null}
        <div className="min-h-0 w-full flex-1">
          <TaxCodesDataTable
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            onSelectionContextChange={clearSelection}
          />
        </div>
      </DataManagementLayout>

      <FiltersDrawer />
      <TaxCodeFormDialog
        isOpen={isDialogOpen}
        taxCodeId={taxCodeId}
        onClose={closeDialog}
      />
      <ConfirmDialog
        confirmText={t(($) => $.list.bulkDelete.confirmButton)}
        content={t(($) => $.list.bulkDelete.confirmContent)}
        isConfirming={isBulkDeletePending}
        isOpen={isBulkDeleteConfirmOpen}
        testId="bulk-delete-tax-codes-confirm-dialog"
        title={t(($) => $.list.bulkDelete.confirmTitle, {
          count: selectedCount,
          formattedCount: formattedSelectedCount,
        })}
        onCancel={closeBulkDeleteConfirm}
        onConfirm={confirmBulkDelete}
      />
    </UniversalFiltersProvider>
  );
}
